import { useState } from 'react';
import { PERMISSIONS, ROLE_DEFAULTS, type Permission } from '@njpos/core';
import { api } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';

type Override = Partial<Record<Permission, boolean>>;

// Staff: who can sign in at a register, what they may do, and their commission. The owner can give one person more or less than their role allows.
export default function Staff() {
  const data = useLoad(() => api<{ users: any[]; stores: { id: string; name: string }[] }>('/api/staff/members'), []);
  const [edit, setEdit] = useState<any>(null);
  const storeName = (id: string) => data.data?.stores.find((s) => s.id === id)?.name ?? '';
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Staff</h1><p>Cashiers and managers sign in at the register with a PIN. Roles set the default permissions; you can change them for one person.</p></div>
        <button className="primary" onClick={() => setEdit({})}>Add staff</button>
      </div>
      <ErrorNote error={data.error} />
      <div className="panel"><div className="panel-body">
        <h2 style={{ margin: '0 0 6px' }}>Who can do what</h2>
        <table className="grid"><tbody>
          <tr><td><b>Register user (cashier)</b></td><td>Sees only the register: ringing sales, the cash drawer, lottery and time clock. Cannot see your costs, reports, staff, bank, payroll or settings.</td></tr>
          <tr><td><b>Manager</b></td><td>The back office: items, prices, buying and orders, bills and the calendar, customers, reports and sales insights. Cannot open the owner's pages below.</td></tr>
          <tr><td><b>Owner</b></td><td>Everything, plus the owner-only pages: Staff, Payroll, Accounting (bank accounts, balancing and checks), Card terminal, Integrations, Settings and paying bills.</td></tr>
        </tbody></table>
      </div></div>
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Name</th><th>Role</th><th>Stores</th><th className="n">Commission</th><th>Permissions</th><th>Status</th><th /></tr></thead>
          <tbody>{data.data?.users.map((u) => {
            const extra = Object.keys(u.permissions ?? {}).length;
            return (
              <tr key={u.id} style={u.active ? undefined : { opacity: 0.5 }}>
                <td style={{ fontWeight: 600 }}>{u.name}</td><td>{u.role}</td><td>{(u.store_ids as string[]).map(storeName).join(', ') || (u.role === 'owner' ? 'All' : '—')}</td>
                <td className="n">{u.commission_pct ? `${u.commission_pct}%` : '—'}</td>
                <td>{u.role === 'owner' ? 'Everything' : extra ? <span className="chip warn">{extra} changed</span> : 'Role default'}</td>
                <td>{u.active ? <span className="chip good">active</span> : <span className="chip bad">off</span>}</td>
                <td>{u.role !== 'owner' && <button onClick={() => setEdit(u)}>Edit</button>}</td>
              </tr>);
          })}</tbody>
        </table>
      </div></div>
      {edit && data.data && <EditStaff user={edit} stores={data.data.stores} onClose={() => setEdit(null)} onDone={() => { setEdit(null); data.reload(); }} />}
    </div>
  );
}

function EditStaff({ user, stores, onClose, onDone }: { user: any; stores: { id: string; name: string }[]; onClose: () => void; onDone: () => void }) {
  const isNew = !user.id;
  const [name, setName] = useState(user.name ?? '');
  const [role, setRole] = useState<'cashier' | 'manager'>(user.role ?? 'cashier');
  const [pin, setPin] = useState('');
  const [active, setActive] = useState(user.active ?? true);
  const [commission, setCommission] = useState(String(user.commission_pct ?? 0));
  const [storeIds, setStoreIds] = useState<string[]>(user.store_ids?.length ? user.store_ids : stores.length === 1 ? [stores[0]!.id] : []);
  const [perms, setPerms] = useState<Override>(user.permissions ?? {});
  const [err, setErr] = useState<unknown>(null);
  const ok = name.trim() && storeIds.length > 0 && (isNew ? /^\d{4,6}$/.test(pin) : pin === '' || /^\d{4,6}$/.test(pin));
  const setPerm = (k: Permission, v: string) => setPerms((p) => { const n = { ...p }; if (v === 'default') delete n[k]; else n[k] = v === 'allow'; return n; });

  async function save() {
    setErr(null);
    const body = { name, role, commissionPct: Number(commission) || 0, permissions: perms, storeIds, ...(pin ? { pin } : {}) };
    try {
      if (isNew) await api('/api/staff/members', { body: { ...body, pin } });
      else await api(`/api/staff/members/${user.id}`, { method: 'PATCH', body: { ...body, active } });
      onDone();
    } catch (e) { setErr(e); }
  }

  return (
    <Modal title={isNew ? 'Add staff' : `Edit ${user.name}`} onClose={onClose} wide footer={<button className="primary" disabled={!ok} onClick={save}>Save</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Name<input autoFocus value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>Role<select value={role} onChange={(e) => setRole(e.target.value as any)}><option value="cashier">Cashier</option><option value="manager">Manager</option></select></label>
        <label>{isNew ? 'PIN (4 to 6 digits)' : 'New PIN (leave blank to keep)'}<input inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
        <label>Commission on sales (%)<input inputMode="decimal" value={commission} onChange={(e) => setCommission(e.target.value.replace(/[^\d.]/g, ''))} /></label>
      </div>
      <div>
        <b>Stores</b>
        <div className="row wrap">{stores.map((s) => (
          <label key={s.id} className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)', gap: 6 }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={storeIds.includes(s.id)} onChange={(e) => setStoreIds(e.target.checked ? [...storeIds, s.id] : storeIds.filter((x) => x !== s.id))} /> {s.name}
          </label>))}</div>
      </div>
      <div>
        <b>What this person may do</b>
        <table className="grid"><tbody>
          {PERMISSIONS.map((p) => {
            const byRole = ROLE_DEFAULTS[role].has(p.key);
            const v = perms[p.key];
            return (
              <tr key={p.key}>
                <td>{p.label}</td>
                <td><select value={v === undefined ? 'default' : v ? 'allow' : 'deny'} onChange={(e) => setPerm(p.key, e.target.value)} style={{ width: 'auto' }}>
                  <option value="default">Role default ({byRole ? 'allowed' : 'not allowed'})</option><option value="allow">Always allow</option><option value="deny">Never allow</option>
                </select></td>
              </tr>);
          })}
        </tbody></table>
      </div>
      {!isNew && (
        <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)', gap: 6 }}>
          <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={active} onChange={(e) => setActive(e.target.checked)} /> Can sign in
        </label>
      )}
    </Modal>
  );
}
